import { MatchPlayerVm } from '../utils/api';

export type LineupLike = {
    id?: number;
    number: number;
    capNumber?: number;
    teamId?: number;
    playerId?: number;
};

export const lineupWearNumber = (player: LineupLike): number =>
    player.capNumber && player.capNumber > 0 ? player.capNumber : player.number;

export const lineupPlayersOnCap = <T extends LineupLike>(
    lineup: T[],
    cap: number,
    teamId?: number
): T[] =>
    lineup.filter((player: T): boolean => {
        if (teamId != null && player.teamId != null && player.teamId !== teamId) {
            return false;
        }
        return player.number === cap || lineupWearNumber(player) === cap;
    });

export const duplicateLineupCaps = <T extends LineupLike>(
    lineup: T[],
    teamId: number
): number[] => {
    const counts = new Map<number, number>();
    lineup.forEach((player: T): void => {
        if (player.teamId !== teamId || !player.playerId) {
            return;
        }
        const cap = lineupWearNumber(player);
        if (cap <= 0) {
            return;
        }
        counts.set(cap, (counts.get(cap) || 0) + 1);
    });
    const duplicate: number[] = [];
    counts.forEach((count: number, cap: number): void => {
        if (count > 1) {
            duplicate.push(cap);
        }
    });
    return duplicate;
};

export const lineupCapHasDuplicate = (
    lineup: LineupLike[],
    cap: number,
    teamId: number
): boolean => duplicateLineupCaps(lineup, teamId).includes(cap);

export const teamHasDuplicateLineupCaps = <T extends LineupLike>(
    lineup: T[],
    teamId: number
): boolean => duplicateLineupCaps(lineup, teamId).length > 0;

export const findLineupForEventCap = (
    lineups: MatchPlayerVm[] | undefined,
    cap: number | undefined,
    teamId: number | undefined
): MatchPlayerVm | undefined => {
    if (!lineups || cap == null || teamId == null) {
        return undefined;
    }
    const matches = lineups.filter(
        (l: MatchPlayerVm): boolean =>
            l.teamId === teamId &&
            (l.number === cap || (l.capNumber != null && l.capNumber > 0 && l.capNumber === cap))
    );
    if (matches.length === 0) {
        return undefined;
    }
    return matches[0];
};

export const lineupPlayersNotShownInSlots = <T extends LineupLike>(
    lineup: T[],
    teamId: number,
    slotLimit: number,
    shownPlayerIds: Set<number>
): T[] =>
    lineup.filter((player: T): boolean => {
        if (!player.playerId || player.teamId !== teamId) {
            return false;
        }
        if (shownPlayerIds.has(player.playerId)) {
            return false;
        }
        const slot = player.number;
        return slot <= 0 || slot > slotLimit;
    });
